import React, { use } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NumericField from 'shared/components/formFields/numeric-field/numeric-field';
import SelectField from 'shared/components/formFields/select-field';
import SoundDetectionLoudSoundOutline from 'shared/components/icons/sound-detection-loud-sound-outline';
import { SonevoSlider } from 'shared/components/slider/sonevo-slider';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import IPUnitProviderContext from 'features/systems/ip-unit/providers/contexts/ip-unit-provider-context';
import styles from './ip-unit-configuration-component.module.scss';

export const RestartTimerConstants = {
  off: 0,
  on: 900,
};

export const IgnoreAcousticAlarmConstants = {
  oneSecondInMilliseconds: 1000,
  thirtySecondsInMs: 30000,
};

export const RssiThresholdConstants = {
  min: 0,
  max: 99,
};

const IPUnitConfigurationComponent = (): React.JSX.Element => {
  const { t } = useTranslation('ipUnit');

  const { setValue, control } = useFormContext();

  const { readIPUnitConfigurationData } = use(IPUnitProviderContext);

  const {
    systemsDetailsHook: { viewingMode },
  } = useSystemsContextProvider();

  const speakerLevelIsChanged = (_event: Event, value: number | number[], _activeThumb: number): void => {
    setValue('volume', value as number, {
      shouldDirty: true,
    });
  };

  const restartTimerOptions = [
    <MenuItem key={`system-restart-timer-off`} value={RestartTimerConstants.off}>
      {t('configuration.inputForm.timer.off')}
    </MenuItem>,
    <MenuItem key={`system-restart-timer-on`} value={RestartTimerConstants.on}>
      {t('configuration.inputForm.timer.on')}
    </MenuItem>,
  ];

  const ignoreNewAcousticAlarmOptions = [
    <MenuItem key={`system-ignore-acoustic-off`} value={IgnoreAcousticAlarmConstants.oneSecondInMilliseconds}>
      {t('configuration.inputForm.ignoreAcoustic.off')}
    </MenuItem>,
    <MenuItem key={`system-ignore-acoustic-thirtySeconds`} value={IgnoreAcousticAlarmConstants.thirtySecondsInMs}>
      {t('configuration.inputForm.ignoreAcoustic.thirtySeconds')}
    </MenuItem>,
  ];

  const speakersMinValue = -12;
  const speakersMaxValue = 6;

  const speakerSliderMarks = [
    { value: -12, label: '-12' },
    { value: -11 },
    { value: -10 },
    { value: -9 },
    { value: -8 },
    { value: -7 },
    { value: -6 },
    { value: -5 },
    { value: -4 },
    { value: -3 },
    { value: -2 },
    { value: -1 },
    { value: 0 },
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
    { value: 6, label: '6' },
  ];

  const getDhcpTimerTranslation = (value: number | undefined): string => {
    if (value && value > 0) {
      return 'configuration.dhcpRestartTimer';
    }
    return 'configuration.dhcpRestartTimerOff';
  };

  const renderViewingConfigurationSettings = (): React.JSX.Element => {
    return (
      <div className={styles.configurationSettings}>
        <Typography variant="h5">{t('configuration.configuration')}</Typography>
        <Typography variant="subtitle1">
          {t('configuration.hostName')} {readIPUnitConfigurationData?.hostName}
        </Typography>

        <Typography variant="subtitle1">
          {t(getDhcpTimerTranslation(readIPUnitConfigurationData?.dhcpRestartTimeAfterLostContact))}
        </Typography>

        <Typography variant="subtitle1">
          {readIPUnitConfigurationData?.ignoreAcousticAlarms &&
          readIPUnitConfigurationData.ignoreAcousticAlarms <= IgnoreAcousticAlarmConstants.oneSecondInMilliseconds
            ? t('configuration.ignoreAcousticAlarmsOff')
            : t('configuration.ignoreAcousticAlarms', {
                ignoreAcousticAlarms:
                  (readIPUnitConfigurationData?.ignoreAcousticAlarms || IgnoreAcousticAlarmConstants.oneSecondInMilliseconds) / 1000,
              })}
        </Typography>

        <Typography variant="subtitle1">
          {t('configuration.rssiThreshold')} {readIPUnitConfigurationData?.rssiThreshold}
        </Typography>
      </div>
    );
  };

  const renderViewingAccousticSettings = (): React.JSX.Element => {
    return (
      <div className={styles.acousticSettings}>
        <SonevoSlider
          label={t('configuration.volume')}
          value={readIPUnitConfigurationData?.volume ?? speakersMinValue}
          disabled={true}
          sliderIconStart={<SoundDetectionLoudSoundOutline className={styles.sliderIconSmall} disabled={true} />}
          sliderIconEnd={<SoundDetectionLoudSoundOutline className={styles.sliderIcon} disabled={true} />}
          sliderMarks={speakerSliderMarks}
          onChange={() => {}}
          minValue={-12}
          maxValue={6}
        />
      </div>
    );
  };

  const renderEditingConfigurationSettings = (): React.JSX.Element => {
    return (
      <div className={styles.configurationSettings}>
        <Typography variant="h5">{t('configuration.configuration')}</Typography>
        <Controller
          name={'hostName'}
          control={control}
          rules={{
            required: t('configuration.inputForm.hostNameRequired'),
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('configuration.inputForm.hostNameLabel')}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: 16 } }}
              error={!!error}
              helperText={error?.message}
              onChange={(e) => {
                const filteredValue = e.target.value.replaceAll(/\W/g, '');
                field.onChange(filteredValue);
              }}
            />
          )}
        />

        <Controller
          name="dhcpRestartTimeAfterLostContact"
          control={control}
          render={({ field }) => (
            <SelectField {...field} label={t('configuration.inputForm.dhcpRestartTimeAfterLostContactInputLabel')} variant="outlined">
              {restartTimerOptions}
            </SelectField>
          )}
        />

        <Controller
          name="ignoreAcousticAlarms"
          control={control}
          render={({ field }) => {
            let acousticValue = field.value;

            const isAcceptedValue =
              acousticValue === IgnoreAcousticAlarmConstants.oneSecondInMilliseconds ||
              acousticValue === IgnoreAcousticAlarmConstants.thirtySecondsInMs;

            if (!isAcceptedValue) {
              acousticValue =
                acousticValue > IgnoreAcousticAlarmConstants.oneSecondInMilliseconds
                  ? setValue('ignoreAcousticAlarms', IgnoreAcousticAlarmConstants.thirtySecondsInMs)
                  : setValue('ignoreAcousticAlarms', IgnoreAcousticAlarmConstants.oneSecondInMilliseconds);
            }

            return (
              <SelectField
                {...field}
                value={acousticValue ?? ''}
                label={t('configuration.inputForm.ignoreAcousticAlarmsInputLabel')}
                variant="outlined"
              >
                {ignoreNewAcousticAlarmOptions}
              </SelectField>
            );
          }}
        />

        <Controller
          name="rssiThreshold"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <NumericField
              field={field}
              fieldName="rssiThreshold"
              label={t('configuration.inputForm.rssiThresholdInputLabel')}
              min={RssiThresholdConstants.min}
              max={RssiThresholdConstants.max}
              step={1}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </div>
    );
  };

  const renderEditingAccousticSettings = (): React.JSX.Element => {
    return (
      <div className={styles.acousticSettings}>
        <Controller
          name="volume"
          control={control}
          render={({ field }) => (
            <SonevoSlider
              label={t('configuration.volume')}
              value={field.value}
              disabled={false}
              onChange={speakerLevelIsChanged}
              sliderIconStart={<SoundDetectionLoudSoundOutline className={styles.sliderIconSmall} disabled={false} />}
              sliderIconEnd={<SoundDetectionLoudSoundOutline className={styles.sliderIcon} disabled={false} />}
              sliderMarks={speakerSliderMarks}
              minValue={speakersMinValue}
              maxValue={speakersMaxValue}
            />
          )}
        />
      </div>
    );
  };

  return (
    <>
      <div className={styles.configuration}>
        {viewingMode === 'viewing' && (
          <>
            {renderViewingConfigurationSettings()}
            {renderViewingAccousticSettings()}
          </>
        )}

        {viewingMode === 'editing' && (
          <>
            {renderEditingConfigurationSettings()}
            {renderEditingAccousticSettings()}
          </>
        )}
      </div>
      <Divider sx={{ width: '100%' }} />
    </>
  );
};

export default IPUnitConfigurationComponent;
